import { ReactNode } from 'react';
import { tokenizeQuery } from './search';

interface Range {
  start: number;
  end: number;
}

interface WordSpan {
  start: number;
  end: number;
  initial: string;
}

// Per-code-unit normalized form of `text` plus a map from normalized index →
// original index. Mirrors the search util's normalization (diacritic strip,
// punctuation → space, lowercase) but omits the whitespace-collapse and trim
// steps so positions round-trip cleanly back to the original text.
const normalizeWithMap = (text: string): { normalized: string; map: number[] } => {
  const chars: string[] = [];
  const map: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const decomposed = text[i].normalize('NFD').replace(/\p{Diacritic}/gu, '');
    for (const dc of decomposed) {
      const emit = /[\p{L}\p{N}\s]/u.test(dc) ? dc.toLowerCase() : ' ';
      chars.push(emit);
      map.push(i);
    }
  }
  return { normalized: chars.join(''), map };
};

// Split on non-letter/non-number runs. `initial` is normalized to match the
// tokens produced by tokenizeQuery, so an acronym check against them is a
// plain substring test on a joined initials string.
const splitIntoWords = (text: string): WordSpan[] => {
  const words: WordSpan[] = [];
  const regex = /[\p{L}\p{N}]+/gu;
  for (const match of text.matchAll(regex)) {
    const raw = match[0];
    const initial = raw[0]
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
    words.push({ start: match.index, end: match.index + raw.length, initial });
  }
  return words;
};

const findSubstringRanges = (token: string, normalized: string, map: number[]): Range[] => {
  const ranges: Range[] = [];
  let searchFrom = 0;
  while (true) {
    const hit = normalized.indexOf(token, searchFrom);
    if (hit < 0) break;
    const start = map[hit];
    let end = map[hit + token.length - 1] + 1;
    // Extend past any trailing normalized code units that still map to the
    // same original char (defensive — matters only if a char decomposes into
    // multiple emitted chars and the match ends mid-decomposition).
    let k = hit + token.length;
    while (k < map.length && map[k] === map[hit + token.length - 1]) {
      end = map[k] + 1;
      k += 1;
    }
    if (end > start) {
      ranges.push({ start, end });
    }
    searchFrom = hit + Math.max(1, token.length);
  }
  return ranges;
};

const findAcronymRanges = (token: string, words: WordSpan[]): Range[] => {
  const initials = words.map(w => w.initial).join('');
  const ranges: Range[] = [];
  let searchFrom = 0;
  while (true) {
    const hit = initials.indexOf(token, searchFrom);
    if (hit < 0) break;
    for (let j = 0; j < token.length; j++) {
      const word = words[hit + j];
      ranges.push({ start: word.start, end: word.end });
    }
    searchFrom = hit + 1;
  }
  return ranges;
};

const mergeRanges = (ranges: Range[]): Range[] => {
  if (ranges.length === 0) return ranges;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: Range[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const cur = sorted[i];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push(cur);
    }
  }
  return merged;
};

// Wraps every occurrence of a query token inside `text` in a `<mark>` element.
// Highlighting is diacritic- and punctuation-insensitive (matches the util's
// search behavior), so `cafe` marks `Café` and `inc` marks the `Inc` in
// `Acme, Inc.`. When a token doesn't appear as a substring at all but does
// match the word-initials of the text, the contributing WORDS are marked
// instead — e.g. `ccp` against `Cross-Channel Pacing` marks all three words.
// Substring highlighting takes precedence per token; acronym-fallback only
// fires when the token has no substring hit at all.
export const highlightMatches = (text: string, query: string): ReactNode => {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return text;

  const { normalized, map } = normalizeWithMap(text);
  const words = splitIntoWords(text);

  const ranges: Range[] = [];
  for (const token of tokens) {
    const substringRanges = findSubstringRanges(token, normalized, map);
    if (substringRanges.length > 0) {
      ranges.push(...substringRanges);
    } else {
      ranges.push(...findAcronymRanges(token, words));
    }
  }

  const merged = mergeRanges(ranges);
  if (merged.length === 0) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < merged.length; i++) {
    const { start, end } = merged[i];
    if (cursor < start) {
      parts.push(text.slice(cursor, start));
    }
    parts.push(<mark key={i}>{text.slice(start, end)}</mark>);
    cursor = end;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
};
